import { AuthenticationService } from './AuthenticationService';
import { AzureAuthenticationService } from './AzureAuthenticationService';
import type { AuthConfig } from '../types';

export class UnifiedAuthenticationService {
  private config: AuthConfig;
  private authService: any = null;
  private provider: 'firebase' | 'azure' | null = null;

  constructor(config: AuthConfig = {}) {
    this.config = { tokenRefreshInterval: 50 * 60 * 1000, logger: console, ...config };
    this.initialize();
  }

  private initialize() {
    const { firebaseAuth, msalConfig, provider } = this.config;
    
    // Respect the explicit provider parameter first
    if (provider === 'firebase' && firebaseAuth) {
      this.initializeFirebase();
    } else if (provider === 'azure' && msalConfig) {
      this.initializeAzure();
    } else if (provider === 'azure' || msalConfig) {
      this.initializeAzure();
    } else if (provider === 'firebase' || firebaseAuth) {
      this.initializeFirebase();
    } else {
      this.config.logger?.error('No authentication provider configured');
    }
  }

  // Method to switch providers dynamically (for mixed mode)
  async switchProvider(newProvider: 'firebase' | 'azure') {
    const { firebaseAuth, msalConfig } = this.config;
    
    // Sign out from current provider
    if (this.authService) {
      await this.signOut();
      this.authService.destroy?.();
      this.authService = null;
    }
    
    // Initialize new provider
    if (newProvider === 'firebase' && firebaseAuth) {
      this.initializeFirebase();
    } else if (newProvider === 'azure' && msalConfig) {
      this.initializeAzure();
    } else {
      this.config.logger?.error(`Cannot switch to ${newProvider} - provider not configured`);
      throw new Error(`Provider ${newProvider} is not configured`);
    }
    
    return this.authService;
  }

  private initializeFirebase() {
    this.provider = 'firebase';
    this.authService = new AuthenticationService({ ...this.config, provider: 'firebase' });
    if ('debug' in (this.config.logger || {}) && typeof (this.config.logger as any).debug === 'function') {
      (this.config.logger as any).debug('Initialized Firebase authentication');
    }
  }

  private initializeAzure() {
    this.provider = 'azure';
    this.authService = new AzureAuthenticationService(this.config);
    if ('debug' in (this.config.logger || {}) && typeof (this.config.logger as any).debug === 'function') {
      (this.config.logger as any).debug('Initialized Azure AD authentication');
    }
  }

  getProvider() {
    return this.provider;
  }
  getAuthService() {
    return this.authService;
  }
  async getToken(forceRefresh = false) {
    if (!this.authService) return null;
    return this.authService.getToken(forceRefresh);
  }
  getUser() {
    if (!this.authService) return null;
    return this.authService.getUser();
  }
  isAuthenticated() {
    if (!this.authService) return false;
    return this.authService.isAuthenticated();
  }
  async signOut() {
    if (!this.authService) return;
    return this.authService.signOut();
  }
  async authenticatedFetch(url: RequestInfo | URL, options: RequestInit = {}) {
    if (!this.authService) throw new Error('No authentication service initialized');
    return this.authService.authenticatedFetch(url, options);
  }
  async loginPopup() {
    if (this.provider !== 'azure' || !this.authService) throw new Error('Login popup is only available for Azure AD');
    return this.authService.loginPopup();
  }
  async loginRedirect() {
    if (this.provider !== 'azure' || !this.authService) throw new Error('Login redirect is only available for Azure AD');
    return this.authService.loginRedirect();
  }
  destroy() {
    if (this.authService) this.authService.destroy();
  }
}
